<template>
	<main>
		<div class="login-wrapper" v-if="!isUserLoggedIn">
			<!-- left side - login form -->
			<div class="login-side">
				<div class="login-content">
					<img class="logo" src="@/assets/images/logo.svg" alt="Landerz logo" />

					<!-- Toggle between Login and Signup -->
					<!-- <div class="auth-toggle">
						<button :class="{ active: !isSignup }" @click="isSignup = false" type="button">Login</button>
						<button :class="{ active: isSignup }" @click="isSignup = true" type="button">Sign Up</button>
					</div> -->

					<!-- Login Form -->
					<!-- <form class="auth-form" @submit.prevent="logIn" v-if="!isSignup" autocomplete="on">
						<fieldset>
							<label for="login-email">Email</label>
							<input
								id="login-email"
								type="email"
								v-model="loginDetails.email"
								aria-label="Email"
								autocomplete="email"
								name="email"
								required
							/>
						</fieldset>

						<fieldset>
							<label for="login-password">Password</label>
							<input
								type="password"
								id="login-password"
								v-model="loginDetails.password"
								aria-label="Password"
								autocomplete="current-password"
								name="password"
								required
							/>
						</fieldset>

						<button type="submit" :disabled="isSubmitting">Log In</button>
					</form> -->

					<!-- Signup Form -->
					<!-- <form class="auth-form" @submit.prevent="signUp" v-if="isSignup" autocomplete="on">
						<fieldset>
							<label for="signup-email">Email</label>
							<input
								id="signup-email"
								type="email"
								v-model="signupDetails.email"
								aria-label="Email"
								autocomplete="email"
								name="email"
								required
							/>
						</fieldset>

						<fieldset>
							<label for="signup-password">Password</label>
							<input
								type="password"
								id="signup-password"
								v-model="signupDetails.password"
								aria-label="Password"
								autocomplete="new-password"
								name="new-password"
								required
							/>
						</fieldset>

						<fieldset>
							<label for="signup-confirm-password">Confirm Password</label>
							<input
								type="password"
								id="signup-confirm-password"
								v-model="signupDetails.confirmPassword"
								aria-label="Confirm Password"
								autocomplete="new-password"
								name="confirm-password"
								required
							/>
						</fieldset>

						<button type="submit" :disabled="isSubmitting">Sign Up</button>
					</form> -->

					<!-- Social Login -->
					<!-- <div class="divider">
						<span>or</span>
					</div> -->

					<button type="button" @click="signInWithGoogle" :disabled="isSubmitting" class="google-btn">
						<AppIcon :name="IconName.Google" :size="20" />
						Sign in with Google
					</button>

					<!-- LinkedIn (feature flag) -->
					<!-- <button
						type="button"
						@click="signInWithLinkedIn"
						:disabled="isSubmitting"
						class="social-login-btn linkedin-btn"
					>
						<AppIcon :name="IconName.LinkedIn" :size="20" />
						Sign in with LinkedIn
					</button> -->

					<!-- Outlook/Microsoft (feature flag) -->
					<button
						type="button"
						@click="signInWithOutlook"
						:disabled="isSubmitting"
						class="social-login-btn outlook-btn"
					>
						<AppIcon :name="IconName.Microsoft" :size="20" />
						Sign in with Outlook
					</button>

					<Button variant="ghost" @click="privacy.openSettings()">{{ $t("privacy.settings") }}</Button>

					<div v-if="infoMessage" class="info-message">
						{{ infoMessage }}
					</div>

					<div v-if="authError" class="error-message">
						{{ authError }}
					</div>
				</div>
			</div>

			<!-- right side - image -->
			<div class="image-side">
				<img src="@/assets/images/terrain-stock.jpg" alt="" />
			</div>
		</div>

		<form class="logout" @submit.prevent="logOut" v-if="isUserLoggedIn">
			<button type="submit">Log Out</button>
		</form>
	</main>
</template>

<script lang="ts" setup>
import { usePrivacyStore } from "@/stores/privacyStore"
import Button from "@/components/ui/Button.vue"
import * as Sentry from "@sentry/browser"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { ref, onMounted, onUnmounted } from "vue"
import { useRouter, useRoute, LocationQueryValue } from "vue-router"
import { useI18n } from "vue-i18n"
import { UseRootStore, RedirectReason } from "@/model/RootStore"
import { storeToRefs } from "pinia"
import { validateIDToken } from "@/utils/ApiClient"
import { analytics } from "@/utils/analytics"
import { supabase } from "@/utils/supabaseClient"
import { finishLoginAndRedirect } from "@/utils/sessionSync"
const privacy = usePrivacyStore()

const store = UseRootStore()
const { isUserLoggedIn } = storeToRefs(store)
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

// const isSignup = ref(false)

// const loginDetails = ref({
// 	email: "",
// 	password: "",
// })

// const signupDetails = ref({
// 	email: "",
// 	password: "",
// 	confirmPassword: "",
// })

const isSubmitting = ref(false)
const authError = ref("")
const infoMessage = ref("")

onMounted(async () => {
	// Check for reason parameter (we won't redirect to login for expired projects or access denied)
	const reason: LocationQueryValue | LocationQueryValue[] = route.query.reason
	if (reason === RedirectReason.LoginRequired) {
		infoMessage.value = t("project.login-required-message")
	}
	// If we are in the logout route then handle the logout
	if (window.location.pathname === "/logout") {
		await handleLogoutFromRoute()
		return
	}

	// Already logged in — sync dashboard cookie if needed, then follow redirect
	const redirectPath = route.query.redirect as string | undefined
	if (store.userApiToken) {
		const result = await finishLoginAndRedirect(store.userApiToken, redirectPath, path => router.push(path))
		if (!result.ok) {
			authError.value = result.error
		}
		return
	}

	// If token was revoked, sign out from Supabase
	const urlParams = new URLSearchParams(window.location.search)
	if (urlParams.get("revoked") === "true") {
		await supabase.auth.signOut()
		urlParams.delete("revoked")
		const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : "")
		window.history.replaceState({}, "", newUrl)
		return
	}

	try {
		// Let's use this in case we are still logged in with Supabase
		const {
			data: { session },
			error,
		} = await supabase.auth.getSession()

		if (error) {
			console.error("Error getting session:", error)
			authError.value = "Session error: " + error.message
			return
		}

		// If we have a session from OAuth callback, validate it with our backend
		if (session?.access_token && !store.userApiToken) {
			await handleOAuthCallback(session.access_token)
		}
	} catch (error) {
		console.error("Error importing Supabase client:", error)
		authError.value = "Error importing Supabase client: " + (error as Error).message
	}

	// Set up auth state listener for OAuth callbacks
	try {
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange(async (event, session) => {
			console.log("Auth state change:", event, session?.user?.email)

			if (event === "SIGNED_IN" && session?.access_token && !store.userApiToken) {
				await handleOAuthCallback(session.access_token)
			}
		})

		// Clean up subscription
		onUnmounted(() => {
			subscription.unsubscribe()
		})
	} catch (error) {
		console.error("Error setting up auth listener:", error)
	}
})

// const logIn = async () => {
// 	isSubmitting.value = true
// 	authError.value = ""

// 	try {
// 		// Everything begins here:
// 		// Step 1: Authenticate with Supabase
// 		const { data, error } = await supabase.auth.signInWithPassword({
// 			email: loginDetails.value.email,
// 			password: loginDetails.value.password,
// 		})

// 		if (error) {
// 			authError.value = error.message
// 			return
// 		}

// 		if (!data.session?.access_token) {
// 			authError.value = "Authentication failed"
// 			return
// 		}

// 		// Step 2: Pass the token to the backend for further validation and also get our own JWT and roles, etc
// 		const validationResult = await validateIDToken(data.session.access_token)

// 		if (!validationResult.success) {
// 			authError.value = validationResult.error || "Backend validation failed"
// 			return
// 		}

// 		// Step 3: We got the token and time to save it and continue
// 		store.userApiToken = validationResult.token
// 		router.push("/")
// 	} catch (error) {
// 		console.error("Error during login:", error)
// 		authError.value = "Login failed. Please try again."
// 	} finally {
// 		isSubmitting.value = false
// 	}
// }

// const signUp = async () => {
// 	isSubmitting.value = true
// 	authError.value = ""

// 	// Validate passwords match
// 	if (signupDetails.value.password !== signupDetails.value.confirmPassword) {
// 		authError.value = "Passwords do not match"
// 		isSubmitting.value = false
// 		return
// 	}

// 	// Validate password strength
// 	if (signupDetails.value.password.length < 6) {
// 		authError.value = "Password must be at least 6 characters long"
// 		isSubmitting.value = false
// 		return
// 	}

// 	try {
// 		// Step 1: Sign up with Supabase
// 		const { data, error } = await supabase.auth.signUp({
// 			email: signupDetails.value.email,
// 			password: signupDetails.value.password,
// 		})

// 		if (error) {
// 			authError.value = error.message
// 			return
// 		}

// 		if (!data.session?.access_token) {
// 			// We should think about this. But that would require doing SMTP configuration which is not difficult
// 			// Find out who is our email sender
// 			authError.value = "Please check your email to confirm your account"
// 			return
// 		}

// 		// Step 2: Validate Supabase token with our backend and get our JWT
// 		const validationResult = await validateIDToken(data.session.access_token)

// 		if (!validationResult.success) {
// 			authError.value = validationResult.error || "Backend validation failed"
// 			return
// 		}

// 		// Step 3: Save our JWT token and redirect
// 		store.userApiToken = validationResult.token
// 		router.push("/")
// 	} catch (error) {
// 		console.error("Error during signup:", error)
// 		authError.value = "Signup failed. Please try again."
// 	} finally {
// 		isSubmitting.value = false
// 	}
// }

const handleLogoutFromRoute = async () => {
	try {
		await store.logout()

		// Should we redirect to login or back to the map?
		router.push("/login")
	} catch (error) {
		console.error("Error during logout from route:", error)
		router.push("/login")
	}
}

const handleOAuthCallback = async (accessToken: string) => {
	if (store.userApiToken) {
		const redirectPath = route.query.redirect as string | undefined
		const result = await finishLoginAndRedirect(store.userApiToken, redirectPath, path => router.push(path))
		if (!result.ok) {
			authError.value = result.error
		}
		return
	}

	isSubmitting.value = true
	authError.value = ""

	try {
		// Step 1: Validate Supabase token with our backend and get our JWT
		const {
			data: { user },
			error: userError,
		} = await supabase.auth.getUser(accessToken)

		if (userError || !user?.email) {
			authError.value = "Failed to get user information"
			return
		}

		// // Step 2: only allow @landerz.ca emails and nothign else
		// if (!user.email || !user.email.endsWith("@landerz.ca")) {
		// 	authError.value = "Only @landerz.ca email addresses are allowed."
		// 	await supabase.auth.signOut()
		// 	return
		// }

		// const emailDomain = user.email.split("@")[1]
		// if (emailDomain !== "landerz.ca") {
		// 	authError.value = "Only @landerz.ca email addresses are allowed."
		// 	await supabase.auth.signOut()
		// 	return
		// }

		// Step 3: Validate Supabase token with our backend and get our JWT
		const validationResult = await validateIDToken(accessToken)

		if (!validationResult.success) {
			authError.value = validationResult.error || "Backend validation failed"
			return
		}

		// Step 4: Save our JWT token and redirect
		store.userApiToken = validationResult.token
		const redirectPath = route.query.redirect as string | undefined
		const result = await finishLoginAndRedirect(validationResult.token, redirectPath, path => router.push(path))
		if (!result.ok) {
			authError.value = result.error
		}
	} catch (error) {
		console.error("Error during OAuth callback:", error)
		Sentry.captureException(error)
		authError.value = "OAuth authentication failed. Please try again."
	} finally {
		isSubmitting.value = false
	}
}

const signInWithGoogle = async () => {
	isSubmitting.value = true
	authError.value = ""

	try {
		// Instead of using Supabase for that we create the Google OAuth URL ourselves
		// We had to use this approach so we could see Landerz on the google consent screen
		// For some reason that I could not figure out, if we use Supabase's built-in Google OAuth
		// it shows XXXXX.supabase.co on the consent screen which is not very professional
		const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
		const redirectUri = `${window.location.origin}/auth/callback`
		const scope = "openid email profile"
		const state = Math.random().toString(36).substring(7)

		if (!clientId) {
			throw new Error("Google Client ID is not configured")
		}

		// keep the state in localStorage for verification and then remove it after in AuthCallbackPage.vue
		localStorage.setItem("oauth_state", state)
		// Store the provider type so we can detect it in the callback
		localStorage.setItem("oauth_provider", "google")

		// Store the redirect parameter if exists, so we can use it after OAuth callback
		const redirectPath = route.query.redirect as string | undefined
		if (redirectPath) {
			localStorage.setItem("oauth_redirect", redirectPath)
		}

		// Build the Google OAuth URL
		const authUrl = new URL("https://accounts.google.com/o/oauth2/v2/auth")
		authUrl.searchParams.set("client_id", clientId)
		authUrl.searchParams.set("redirect_uri", redirectUri)
		authUrl.searchParams.set("response_type", "code")
		authUrl.searchParams.set("scope", scope)
		authUrl.searchParams.set("state", state)
		authUrl.searchParams.set("access_type", "offline")
		authUrl.searchParams.set("prompt", "consent")

		analytics.signInInitiated({ provider: "google" })

		// OAuth here we come
		window.location.href = authUrl.toString()
	} catch (error) {
		console.error("Error during Google sign in:", error)
		Sentry.captureException(error)
		authError.value = "Google sign in failed for some reason. Please try again."
		isSubmitting.value = false
	}
}

const signInWithOutlook = async () => {
	isSubmitting.value = true
	authError.value = ""

	try {
		const clientId = import.meta.env.VITE_OUTLOOK_CLIENT_ID
		const redirectUri = `${window.location.origin}/auth/callback`
		const scope = "openid email profile"
		const state = Math.random().toString(36).substring(7)

		if (!clientId) {
			throw new Error("Outlook Client ID is not configured")
		}

		// keep the state in localStorage for verification and then remove it after in AuthCallbackPage.vue
		localStorage.setItem("oauth_state", state)
		// Store the provider type so we can detect it in the callback
		localStorage.setItem("oauth_provider", "outlook")

		// Store the redirect parameter if exists, so we can use it after OAuth callback
		const redirectPath = route.query.redirect as string | undefined
		if (redirectPath) {
			localStorage.setItem("oauth_redirect", redirectPath)
		}

		// Build the Microsoft OAuth URL
		const authUrl = new URL("https://login.microsoftonline.com/common/oauth2/v2.0/authorize")
		authUrl.searchParams.set("client_id", clientId)
		authUrl.searchParams.set("redirect_uri", redirectUri)
		authUrl.searchParams.set("response_type", "code")
		authUrl.searchParams.set("scope", scope)
		authUrl.searchParams.set("state", state)
		authUrl.searchParams.set("response_mode", "query")

		analytics.signInInitiated({ provider: "outlook" })

		// OAuth here we come
		window.location.href = authUrl.toString()
	} catch (error) {
		console.error("Error during Outlook sign in:", error)
		Sentry.captureException(error)
		authError.value = "Outlook sign in failed for some reason. Please try again."
		isSubmitting.value = false
	}
}

// const signInWithLinkedIn = () => {
// 	console.log("LinkedIn sign in - not implemented yet")
// }

async function logOut() {
	await store.logout()
	router.push("/login")
}
</script>

<style scoped>
main {
	min-height: 100vh;
	margin: 0;
	padding: 0;
}

.login-wrapper {
	display: flex;
	min-height: 100vh;
}

/* left side */
.login-side {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	background: white;
	padding: var(--spacing-xl);

	.login-content {
		width: 100%;
		max-width: 400px;
		display: flex;
		flex-direction: column;
		align-items: center;

		.logo {
			width: 180px;
			margin-bottom: var(--spacing-4xl);
			margin-left: var(--spacing-sm);
		}

		.google-btn,
		.social-login-btn {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: var(--spacing-element-gap);
			width: 100%;
			padding: var(--spacing-md) var(--spacing-lg);
			margin-bottom: var(--spacing-md);
			border: var(--border-width-thin) solid #ddd;
			border-radius: var(--border-radius-base);
			background: var(--neutral-0);
			color: var(--neutral-900);
			font-size: var(--font-size-base);
			font-weight: var(--font-weight-medium);
			cursor: pointer;
			transition: all 0.2s ease;
		}

		.google-btn:hover:not(:disabled),
		.social-login-btn:hover:not(:disabled) {
			background: var(--neutral-0);
			border-color: var(--neutral-200);
			box-shadow: var(--shadow-lg);
		}

		.google-btn:disabled,
		.social-login-btn:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}

		.google-btn svg,
		.social-login-btn svg {
			flex-shrink: 0;
		}

		.info-message {
			background-color: var(--accent-info-background);
			color: var(--accent-info);
			padding: var(--spacing-md) var(--spacing-lg);
			border-radius: var(--border-radius-base);
			margin-bottom: var(--spacing-lg);
			text-align: center;
			font-size: var(--font-size-base);
			width: 100%;
		}

		.error-message {
			color: var(--accent-warning);
			margin-top: var(--spacing-md);
			text-align: center;
			font-size: var(--font-size-base);
		}
	}
}

/* right side */
.image-side {
	flex: 1;
	position: relative;
	overflow: hidden;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
}

/* logout */
.logout {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 100vh;
	padding: var(--spacing-xl);

	button {
		padding: var(--spacing-md) var(--spacing-xl);
		background: var(--primary-green);
		color: var(--neutral-0);
		border: none;
		border-radius: var(--border-radius-md);
		font-size: var(--font-size-medium);
		font-weight: var(--font-weight-medium);
		cursor: pointer;
		transition: background 0.2s ease;

		&:hover {
			background: var(--primary-green);
			box-shadow: var(--shadow-md);
		}
	}
}

/* responsive */
@media (max-width: 768px) {
	.login-wrapper {
		flex-direction: column;
	}

	.image-side {
		display: none;
	}

	.login-side {
		flex: 1;
	}
}
</style>
